import { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Tooltip,
  Button,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUiStore } from '@/store/uiStore';
import useDebounce from '@/hooks/useDebounce';
import { ROUTES } from '@/routes/paths';
import env from '@/config/env';

/** Search field that syncs with the `?q=` param when on Documents, or jumps there on submit. */
function GlobalSearch() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const onDocuments = location.pathname.startsWith('/documents') && !location.pathname.match(/\/documents\/.+/);
  const inputRef = useRef(null);

  const [value, setValue] = useState(onDocuments ? searchParams.get('q') || '' : '');
  const debounced = useDebounce(value, 300);

  // Keep the input in sync when the URL's q changes externally (e.g. back/forward).
  useEffect(() => {
    if (onDocuments) setValue(searchParams.get('q') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDocuments]);

  // Live-filter while already on the Documents list.
  useEffect(() => {
    if (!onDocuments) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debounced) next.set('q', debounced);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // Press "/" anywhere to focus search.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!onDocuments) navigate(`${ROUTES.DOCUMENTS}?q=${encodeURIComponent(value)}`);
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        height: 40,
        width: { xs: '100%', sm: 320, md: 380 },
        borderRadius: 2.5,
        bgcolor: 'background.neutral',
        border: (t) => `1px solid ${t.palette.divider}`,
        transition: 'border-color .2s, box-shadow .2s',
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}1F`,
        },
      }}
    >
      <SearchRoundedIcon fontSize="small" color="action" />
      <InputBase
        inputRef={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search chapters…"
        sx={{ flex: 1, fontSize: 14 }}
      />
      {value ? (
        <IconButton size="small" onClick={() => setValue('')} sx={{ p: 0.25 }}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : (
        <Box
          sx={{
            px: 0.75,
            py: 0.1,
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
            color: 'text.disabled',
            fontSize: 12,
            fontWeight: 600,
            display: { xs: 'none', md: 'block' },
          }}
        >
          /
        </Box>
      )}
    </Box>
  );
}

/** Top app bar: nav toggle, global search, upload CTA, and theme switch. */
export default function Topbar({ onToggleNav, navWidth }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const colorMode = useUiStore((s) => s.colorMode);
  const toggleColorMode = useUiStore((s) => s.toggleColorMode);
  const [mobileSearch, setMobileSearch] = useState(false);

  return (
    <AppBar position="fixed" sx={{ width: { md: `calc(100% - ${navWidth}px)` }, ml: { md: `${navWidth}px` } }}>
      <Toolbar sx={{ gap: 1.5 }}>
        <IconButton edge="start" onClick={onToggleNav} aria-label="toggle navigation">
          {isDesktop ? <MenuOpenRoundedIcon /> : <MenuRoundedIcon />}
        </IconButton>

        {/* Desktop / tablet inline search */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <GlobalSearch />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            onClick={() => setMobileSearch((v) => !v)}
            aria-label="search"
          >
            <SearchRoundedIcon />
          </IconButton>

          {env.uploadEnabled && (
            <>
              <Button
                variant="contained"
                startIcon={<CloudUploadRoundedIcon />}
                onClick={() => navigate(`${ROUTES.DOCUMENTS}?upload=1`)}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Upload
              </Button>
              <IconButton
                color="primary"
                sx={{ display: { xs: 'inline-flex', sm: 'none' }, bgcolor: (t) => `${t.palette.primary.main}14` }}
                onClick={() => navigate(`${ROUTES.DOCUMENTS}?upload=1`)}
                aria-label="upload"
              >
                <CloudUploadRoundedIcon />
              </IconButton>
            </>
          )}

          <Tooltip title={colorMode === 'light' ? 'Dark mode' : 'Light mode'}>
            <IconButton onClick={toggleColorMode} aria-label="toggle theme">
              {colorMode === 'light' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>

      {/* Mobile expandable search row */}
      {mobileSearch && (
        <Box sx={{ px: 2, pb: 1.5, display: { xs: 'block', sm: 'none' } }}>
          <GlobalSearch />
        </Box>
      )}
    </AppBar>
  );
}
